import { useCallback, useState } from 'react';
import { NotifiableError } from '@bugsnag/js';
import { bugtracker } from '@99designs/design-services-common';
import { DS_BRIEF_MULTI_STEP_URL, __, __url } from '@99designs/i18n';
import { useBriefConfig } from '../../context';
import { DuplicateBriefPurchaseError } from '../../lib/errors';
import { hasRequestCartFeatureFlag } from '../../lib/experiments';
import { canUseCart } from '../../lib/hooks/useCart';
import { useNavigateWithLang } from '../../lib/hooks/useNavigateWithLang';
import { BriefQuery } from '../BriefForm/brief.generated';
import { useAddBriefToCart } from './useAddBriefToCart';
import { useBriefSubmit } from './useBriefSubmit';
import { useCartlessSubmit } from './useCartlessSubmit';
import { useSubmitToRequests } from './useSubmitToRequests';

interface HandleSubmitProps {
    briefId: string;
    locale: string;
    authorizationHeader: string;
    shopperId: string;
    willSkipCart: boolean;
}

export type SubmitState =
    | 'idle'
    | 'submitting'
    | 'duplicate'
    | 'invalid-shopper'
    | 'success'
    | 'error';

export interface UseHandleSubmitReturn {
    state: SubmitState;
    loadingCopy: string;
    currentUrl: string;
    mpvId: string;
    handleSubmit: (data: BriefType) => Promise<void>;
}

export type BriefType = BriefQuery['brief'];

export const useHandleSubmit = ({
    briefId,
    locale,
    authorizationHeader,
    shopperId,
    willSkipCart,
}: HandleSubmitProps): UseHandleSubmitReturn => {
    const [state, setState] = useState<SubmitState>('idle');
    const [loadingCopy, setLoadingCopy] = useState(
        willSkipCart ? '' : __('Adding project to your cart')
    );
    const [currentUrl, setCurrentUrl] = useState('');
    const [mpvId, setMpvId] = useState('');

    const navigate = useNavigateWithLang();
    const submitWithoutCart = useCartlessSubmit(); // Cartless submission for Prepress and Upload & Go requests
    const addBriefToCart = useAddBriefToCart(locale, authorizationHeader, shopperId, briefId); // Community
    const briefSubmit = useBriefSubmit(locale); // New submit path with cart
    const submitToRequests = useSubmitToRequests(locale, authorizationHeader, shopperId, briefId); // CARE Collaborate

    const briefConfig = useBriefConfig();

    const handleSubmit = useCallback(
        async (data: BriefType) => {
            try {
                setState('submitting');

                if (!canUseCart(shopperId)) {
                    const copy = `Users with internal shopper ids like ${shopperId} cannot checkout. Please log out and log back in using a shopper account`;
                    alert(copy);
                    setLoadingCopy(copy);
                    setState('invalid-shopper');
                    return;
                }

                if (!willSkipCart) {
                    setLoadingCopy(__('Taking you to your cart'));
                }

                if (!data) {
                    throw new Error(`Failed to retrieve request data for brief ${briefId}`);
                }

                if (isMultiBriefPurchaseAttempt(data)) {
                    bugtracker.notify(
                        new DuplicateBriefPurchaseError(
                            `Encountered duplicate purchase attempt for brief in useHandleSubmit hook: ${briefId}`
                        )
                    );

                    setState('duplicate');
                    setMpvId(data.fulfilment?.mpvId ?? '');
                    setCurrentUrl(data.request?.facts?.currentUrl ?? '');

                    return;
                }

                if (isBifrostBrief(data)) {
                    await addBriefToCart(data);
                    setState('success');
                    window.location.replace(__url('/c'));
                    return;
                }

                if (!data.config.submitWithCart) {
                    const nextUrl = await submitWithoutCart(data);

                    setState('success');
                    window.location.replace(
                        briefConfig.autoFulfilment ? `http://localhost:3001${nextUrl}` : nextUrl
                    );

                    return;
                }

                const isProductEligibleForRequestsCart =
                    data.request?.expertService?.isOverflowEligible;

                if (isProductEligibleForRequestsCart && (await hasRequestCartFeatureFlag())) {
                    await briefSubmit(data);
                } else {
                    await submitToRequests(data); // Fulfil via Requests if Requests provisioned the brief.
                }
                setState('success');
                if (!briefConfig.autoFulfilment) {
                    window.location.replace(__url('/c'));
                }
                return;
            } catch (e) {
                bugtracker.notify(e as NotifiableError, (event) => {
                    event.context = `Error during brief submission for briefId: ${briefId}.`;
                });
                setState('error');
                navigate(DS_BRIEF_MULTI_STEP_URL + '/error');
                throw e;
            }
        },
        [
            briefId,
            addBriefToCart,
            briefSubmit,
            submitToRequests,
            submitWithoutCart,
            willSkipCart,
            shopperId,
            briefConfig,
            navigate,
        ]
    );

    return {
        state,
        loadingCopy,
        currentUrl,
        mpvId,
        handleSubmit,
    };
};

function isBifrostBrief(data: BriefType) {
    // Bifrost briefs will not have a request object and will also have a fulfilment strategy of COMMUNITY
    // There may be some old briefs from before RnB that don't have a request object but are not Bifrost briefs
    return data.request == null && data.fulfilmentStrategy === 'FULFILMENT_STRATEGY_COMMUNITY';
}

function isMultiBriefPurchaseAttempt(data: BriefType) {
    // A brief is considered purchased if it has an order associated with it (Bifrost) or if it has a request that is not in DRAFT or IN-PROGRESS state (RnB)
    return (
        (isBifrostBrief(data) && data.order?.id != null) ||
        (data.request != null &&
            data.request?.state !== 'DRAFT' &&
            data.request?.state !== 'IN_PROGRESS')
    );
}
